/**
 * video-meta.js — structured video container metadata for video-location.
 *
 * Why this exists:
 *   The old extractor (_findMP4GPS in geo-analyzer.js) brute-force scanned
 *   up to 50 MB of raw bytes for the ©xyz marker. That found only one GPS
 *   shape, could false-positive inside compressed mdat, and never read the
 *   capture date, codecs, fps or device info the UI promised.
 *
 *   This module does a real ISO-BMFF (MP4/MOV) parse:
 *     • track info (codec / fps / resolution / audio) via the already
 *       vendored MP4Box.js wrapper (__Mp4BoxProbe) — no reinvented decoder;
 *     • a focused, bounded box-tree walk of the `moov` atom for the parts
 *       MP4Box does not surface: GPS (udta/©xyz + meta/keys+ilst ISO6709),
 *       real capture date (com.apple.quicktime.creationdate / ©day / mvhd),
 *       and device make / model / software.
 *
 *   The walk only ever reads `moov` (located by walking top-level box
 *   headers and skipping `mdat` by size) — it never scans media payload.
 *
 * Usage:
 *   const m = await window.VideoMeta.parse(file);
 *   // → { container, duration, width, height, video, audio, bitrate,
 *   //     gps, captureDate, captureDateRaw, captureDateSource, device }
 *   Never throws — on a non-MP4 / unparsable file it returns the struct
 *   with nulls so the caller can fall back to the HTML5 <video> element.
 */
(function () {
'use strict';

// Seconds between the QuickTime epoch (1904-01-01) and the Unix epoch.
var QT_EPOCH_OFFSET = 2082844800;
// moov is normally < 2 MB; cap the slice so a pathological file can't OOM.
var MAX_MOOV_BYTES = 64 * 1024 * 1024;

/* ───────────────── ISO-BMFF primitives ───────────────── */

function fourcc(dv, off) {
    return String.fromCharCode(dv.getUint8(off), dv.getUint8(off + 1),
                               dv.getUint8(off + 2), dv.getUint8(off + 3));
}

function utf8(buf, off, len) {
    if (len <= 0) return '';
    try {
        return new TextDecoder('utf-8')
            .decode(new Uint8Array(buf, off, len))
            .replace(/\u0000+$/, '').trim();
    } catch (e) {
        var s = '', dv = new DataView(buf);
        for (var i = 0; i < len; i++) {
            var c = dv.getUint8(off + i);
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s.trim();
    }
}

/**
 * Iterate the boxes in [start, end). cb(type, payloadStart, payloadEnd,
 * boxStart, headerSize). Stops on the first malformed/truncated header so a
 * corrupt tail can never run the loop away.
 */
function eachBox(dv, start, end, cb) {
    var off = start, guard = 0;
    while (off + 8 <= end && guard++ < 100000) {
        var size = dv.getUint32(off);
        var type = fourcc(dv, off + 4);
        var headerSize = 8;
        if (size === 1) {
            // 64-bit largesize
            size = dv.getUint32(off + 8) * 4294967296 + dv.getUint32(off + 12);
            headerSize = 16;
        } else if (size === 0) {
            size = end - off; // box runs to the end of its parent
        }
        if (size < headerSize || off + size > end) break;
        cb(type, off + headerSize, off + size, off, headerSize);
        off += size;
    }
}

/* ───────────────── moov metadata extractor ───────────────── */

function parseISO6709(str, res) {
    if (!str || res.gps) return; // first valid hit wins (©xyz and keys agree)
    var m = str.match(/([+-]\d+(?:\.\d+)?)([+-]\d+(?:\.\d+)?)(?:([+-]\d+(?:\.\d+)?))?/);
    if (!m) return;
    var lat = parseFloat(m[1]), lon = parseFloat(m[2]);
    if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
    res.gps = { lat: lat, lon: lon };
    if (m[3] != null) {
        var alt = parseFloat(m[3]);
        if (isFinite(alt)) res.gps.alt = alt;
    }
}

function setDate(res, str, source) {
    if (!str) return;
    // QuickTime creationdate is the richest (carries the timezone) — once we
    // have it, a plain ©day string must not overwrite it.
    if (res.captureDateSource === 'quicktime' && source !== 'quicktime') return;
    var d = new Date(str);
    if (isNaN(d.getTime())) {
        // EXIF-style "2024:08:15 14:30:00"
        var m = str.match(/(\d{4})[:\-](\d{2})[:\-](\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
        if (m) d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    }
    if (isNaN(d.getTime())) return;
    res._date = d;
    res.captureDateRaw = str;
    res.captureDateSource = source;
}

// QuickTime classic udta text item: [uint16 byteLen][uint16 lang][string].
// Some writers omit the prefix — fall back to the whole payload as text.
function readUdtaText(buf, dv, ps, pe) {
    var len = pe - ps;
    if (len >= 4) {
        var slen = dv.getUint16(ps);
        if (slen > 0 && slen <= len - 4) return utf8(buf, ps + 4, slen);
    }
    return utf8(buf, ps, len);
}

// ilst `data` box: [uint32 typeIndicator][uint32 locale][value].
function readDataBox(buf, dv, ps, pe) {
    if (pe - ps < 8) return '';
    var ind = dv.getUint32(ps), vs = ps + 8;
    if (ind === 1) return utf8(buf, vs, pe - vs);          // UTF-8 text
    if (ind === 21 || ind === 22) {                        // signed/unsigned int
        var n = 0;
        for (var i = vs; i < pe; i++) n = n * 256 + dv.getUint8(i);
        return String(n);
    }
    return utf8(buf, vs, pe - vs);                         // best-effort text
}

// 3GPP `loci` location box (FullBox). This is what ffmpeg's mov muxer and
// many Android recorders write — note the field order is longitude, then
// latitude, then altitude, each a signed 16.16 fixed-point number.
function parseLoci(dv, ps, pe, res) {
    if (res.gps) return;
    var off = ps + 4 + 2; // skip version+flags(4) and language(2)
    // name: null-terminated UTF-8 string (usually empty → a single \0)
    var guard = 0;
    while (off < pe && dv.getUint8(off) !== 0 && guard++ < 4096) off++;
    off += 1;             // consume the \0 terminator
    off += 1;             // role (1 byte)
    if (off + 12 > pe) return;
    var lon = dv.getInt32(off) / 65536;
    var lat = dv.getInt32(off + 4) / 65536;
    var alt = dv.getInt32(off + 8) / 65536;
    if (isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
        res.gps = { lat: lat, lon: lon };
        if (alt) res.gps.alt = alt;
    }
}

function isKnownMetaChild(t) {
    return t === 'hdlr' || t === 'keys' || t === 'ilst' || t === 'dinf' ||
           t === 'iloc' || t === 'iinf' || t === 'pitm' || t === 'idat' ||
           t === 'free' || t === 'ctry' || t === 'lang';
}

function extractMoovMeta(buf) {
    var dv = new DataView(buf);
    var res = {
        gps: null, _date: null, captureDateRaw: null, captureDateSource: null,
        mvhdTime: 0, device: { make: '', model: '', software: '' },
        locationWiped: false
    };

    /* Android hands a browser a COPY of the video with the location boxes
     * (loci, ©xyz, gps, gps0) zeroed from their type field to their end — the
     * size field stays, so the walk still steps over a box whose type is four
     * NULs and whose payload is all zero (AOSP RedactionUtils: boxTypeOffset =
     * data − 4). No writer produces that; it is the wipe, and it means the
     * original on the phone still has the place. See docs/why/photo-location.md. */
    function isWipedBox(type, cs, ce) {
        if (type !== '\0\0\0\0' || ce <= cs) return false;
        for (var i = cs; i < ce; i++) if (dv.getUint8(i) !== 0) return false;
        return true;
    }
    var keysList = [];       // keys box: ordered key names
    var ilstByIndex = {};    // ilst numeric items: { 1-based index: value }

    function applyKeyValue(name, val) {
        if (val == null || val === '') return;
        var n = String(name).toLowerCase();
        if (n.indexOf('iso6709') !== -1) parseISO6709(val, res);
        else if (n.indexOf('creationdate') !== -1) setDate(res, val, 'quicktime');
        else if (n.indexOf('.make') !== -1) res.device.make = res.device.make || val;
        else if (n.indexOf('.model') !== -1) res.device.model = res.device.model || val;
        else if (n.indexOf('.software') !== -1) res.device.software = res.device.software || val;
    }

    function parseKeys(cs, ce) {
        var off = cs + 8; // skip version+flags(4) and entry_count(4)
        while (off + 8 <= ce) {
            var ksize = dv.getUint32(off);
            if (ksize < 8 || off + ksize > ce) break;
            keysList.push(utf8(buf, off + 8, ksize - 8)); // skip key_namespace(4)
            off += ksize;
        }
    }

    function parseIlst(cs, ce) {
        eachBox(dv, cs, ce, function (itemType, ps, pe, boxStart) {
            var val = null;
            eachBox(dv, ps, pe, function (t, ps2, pe2) {
                if (t === 'data') val = readDataBox(buf, dv, ps2, pe2);
            });
            if (val == null) return;
            if (itemType.charCodeAt(0) === 0) {
                // numeric index item — resolved against keysList after the walk
                ilstByIndex[dv.getUint32(boxStart + 4)] = val;
            } else if (itemType === '\xA9xyz' || itemType === 'xyz ') {
                parseISO6709(val, res);
            } else if (itemType === '\xA9day' || itemType === 'date') {
                setDate(res, val, 'day');
            } else if (itemType === '\xA9mak') {
                res.device.make = res.device.make || val;
            } else if (itemType === '\xA9mod') {
                res.device.model = res.device.model || val;
            } else if (itemType === '\xA9swr' || itemType === '\xA9too') {
                res.device.software = res.device.software || val;
            }
        });
    }

    function walkMeta(ps, pe) {
        // ISO `meta` is a FullBox (4 bytes version+flags before children);
        // QuickTime `meta` is a plain container. Detect by peeking: if a valid
        // known child box sits right at `ps`, it's the QuickTime layout.
        var childStart = ps;
        if (pe - ps >= 8) {
            var t = fourcc(dv, ps + 4);
            if (!isKnownMetaChild(t)) childStart = ps + 4; // FullBox → skip flags
        }
        eachBox(dv, childStart, pe, function (type, cs, ce) {
            if (type === 'keys') parseKeys(cs, ce);
            else if (type === 'ilst') parseIlst(cs, ce);
        });
    }

    function walkUdta(ps, pe) {
        eachBox(dv, ps, pe, function (type, cs, ce) {
            if (type === '\xA9xyz' || type === 'xyz ') {
                parseISO6709(readUdtaText(buf, dv, cs, ce), res);
            } else if (type === '\xA9day' || type === 'date') {
                setDate(res, readUdtaText(buf, dv, cs, ce), 'day');
            } else if (type === '\xA9mak' || type === 'mak ') {
                res.device.make = res.device.make || readUdtaText(buf, dv, cs, ce);
            } else if (type === '\xA9mod' || type === 'mod ') {
                res.device.model = res.device.model || readUdtaText(buf, dv, cs, ce);
            } else if (type === '\xA9swr' || type === '\xA9too') {
                res.device.software = res.device.software || readUdtaText(buf, dv, cs, ce);
            } else if (type === 'loci') {
                parseLoci(dv, cs, ce, res);
            } else if (type === 'meta') {
                walkMeta(cs, ce);
            } else if (isWipedBox(type, cs, ce)) {
                res.locationWiped = true;
            }
        });
    }

    function walkMoov(ps, pe) {
        eachBox(dv, ps, pe, function (type, cs, ce) {
            if (type === 'mvhd') {
                var version = dv.getUint8(cs);
                res.mvhdTime = version === 1
                    ? dv.getUint32(cs + 4) * 4294967296 + dv.getUint32(cs + 8)
                    : dv.getUint32(cs + 4);
            } else if (type === 'udta') {
                walkUdta(cs, ce);
            } else if (type === 'meta') {
                walkMeta(cs, ce);
            } else if (type === 'trak') {
                // per-track udta (rare, but some action cams write GPS here)
                eachBox(dv, cs, ce, function (t2, cs2, ce2) {
                    if (t2 === 'udta') walkUdta(cs2, ce2);
                    else if (t2 === 'meta') walkMeta(cs2, ce2);
                });
            }
        });
    }

    eachBox(dv, 0, buf.byteLength, function (type, cs, ce) {
        if (type === 'moov') walkMoov(cs, ce);
    });

    // Resolve numeric ilst items against the keys table (ilst index is 1-based).
    keysList.forEach(function (name, i) {
        if (ilstByIndex[i + 1] != null) applyKeyValue(name, ilstByIndex[i + 1]);
    });

    return res;
}

/* ───────────────── moov locator (header-only walk) ───────────────── */

async function locateAndReadMoov(file) {
    var offset = 0, size = file.size, guard = 0;
    while (offset + 16 <= size && guard++ < 100000) {
        var hdr = new DataView(await file.slice(offset, offset + 16).arrayBuffer());
        var boxSize = hdr.getUint32(0);
        var type = String.fromCharCode(hdr.getUint8(4), hdr.getUint8(5),
                                       hdr.getUint8(6), hdr.getUint8(7));
        var headerSize = 8;
        if (boxSize === 1) {
            boxSize = hdr.getUint32(8) * 4294967296 + hdr.getUint32(12);
            headerSize = 16;
        } else if (boxSize === 0) {
            boxSize = size - offset;
        }
        if (boxSize < headerSize) break;
        if (type === 'moov') {
            var end = Math.min(offset + boxSize, offset + MAX_MOOV_BYTES, size);
            return await file.slice(offset, end).arrayBuffer();
        }
        offset += boxSize; // skip mdat/free/etc by size — never read their bytes
    }
    return null;
}

/* ───────────────── public API ───────────────── */

function guessContainer(file) {
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext === 'webm') return 'webm';
    if (ext === 'mkv') return 'mkv';
    if (ext === 'avi') return 'avi';
    return 'other';
}

var VideoMeta = {
    parse: async function (file) {
        var result = {
            container: 'unknown',
            duration: 0, width: 0, height: 0,
            video: null, audio: null, bitrate: 0,
            gps: null,
            captureDate: null, captureDateRaw: null, captureDateSource: null,
            device: { make: '', model: '', software: '' },
            parsed: false
        };
        if (!file || typeof file.slice !== 'function') return result;

        // 1. Track info via the shared MP4Box wrapper.
        try {
            var probe = await window.__Mp4BoxProbe(file);
            result.container = 'mp4';
            result.duration = probe.duration || 0;
            result.width = probe.width || 0;
            result.height = probe.height || 0;
            var tracks = probe.tracks || [];
            var v = tracks.filter(function (t) { return t.type === 'video'; })[0];
            var a = tracks.filter(function (t) { return t.type === 'audio'; })[0];
            if (v) {
                var vdur = v.timescale ? v.duration / v.timescale : result.duration;
                result.video = {
                    codec: v.codec || '',
                    fps: (v.nb_samples && vdur) ? v.nb_samples / vdur : 0,
                    width: (v.video && v.video.width) || v.track_width || result.width,
                    height: (v.video && v.video.height) || v.track_height || result.height
                };
            }
            if (a) {
                result.audio = {
                    codec: a.codec || '',
                    sampleRate: (a.audio && a.audio.sample_rate) || 0,
                    channels: (a.audio && a.audio.channel_count) || 0
                };
            }
            if (result.duration > 0) {
                result.bitrate = Math.round(file.size * 8 / result.duration);
            }
        } catch (e) {
            // Not a parsable MP4/MOV — webm/avi/mkv. The caller fills duration
            // and dimensions from the HTML5 <video> element.
            result.container = guessContainer(file);
        }

        // 2. moov metadata walk (MP4 family only).
        if (result.container === 'mp4') {
            try {
                var moov = await locateAndReadMoov(file);
                if (moov) {
                    var meta = extractMoovMeta(moov);
                    result.gps = meta.gps;
                    result.device = meta.device;
                    result.locationWiped = !meta.gps && meta.locationWiped;
                    if (meta._date) {
                        result.captureDate = meta._date;
                        result.captureDateRaw = meta.captureDateRaw;
                        result.captureDateSource = meta.captureDateSource;
                    } else if (meta.mvhdTime > QT_EPOCH_OFFSET) {
                        // mvhd creation_time is seconds since 1904; treat 0 or a
                        // sub-epoch value as "writer left it blank".
                        var d = new Date((meta.mvhdTime - QT_EPOCH_OFFSET) * 1000);
                        var yr = d.getFullYear();
                        if (yr > 1990 && yr < 2100) {
                            result.captureDate = d;
                            result.captureDateRaw = d.toISOString();
                            result.captureDateSource = 'mvhd';
                        }
                    }
                }
            } catch (e2) { /* metadata walk failed — keep the track info */ }
        }

        result.parsed = true;
        return result;
    },

    // Exposed for unit tests — parse a raw moov ArrayBuffer directly.
    _extractMoovMeta: extractMoovMeta
};

if (typeof window !== 'undefined') window.VideoMeta = VideoMeta;

// CommonJS hook so the node unit test can import the pure parser.
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { extractMoovMeta: extractMoovMeta, eachBox: eachBox, VideoMeta: VideoMeta };
}

})();
